/**
 * Visit categories on a plot's categorical axis.
 *
 * Two defects motivate this module, both reported against the Clario tenant:
 *
 * 1. **Duplicate bars.** A trial's study design can carry the same visit twice
 *    under different casing — CYTB323K12201 has both `SCREENING` and
 *    `Screening` as separate `trial_visit_type` rows because two source systems
 *    feed it. The plot layer dedupes categories with a raw `Set`, so the chart
 *    grew two "Screening" bars. Folding case and surrounding whitespace merges
 *    them back into one.
 *
 * 2. **Visits not in timeline order.** Category order used to be whatever order
 *    rows happened to arrive in, i.e. alphabetical for most datasets, so a
 *    visit axis read `SCREENING, V1, W12, W16, WEEK 4…`. The backend serializes
 *    `visit_order` (the protocol position from `trial_visit_type`) on the
 *    visit-based datasets; ordering by it puts the axis in protocol order.
 *    `days` is only a fallback: it is a cumulative sum of `interval_days` and
 *    collapses to a constant for study designs that leave the intervals blank.
 */

/** Row shape this module reads. Plot rows are loosely typed elsewhere. */
type PlotRow = Record<string, unknown>;

const VISIT_AXES = new Set([
  "visit_type",
  "timepoint",
  // subjects_overview's Visit Type (Source) chart — same visit_order sidecar
  // convention as the other visit-based datasets (see module docstring).
  "trial_visit_type_source_value",
]);

/** Whether a categorical axis holds visit labels. */
export const isVisitAxis = (dataMapping?: string): boolean =>
  Boolean(dataMapping) && VISIT_AXES.has(String(dataMapping).toLowerCase());

/** Key that treats labels differing only in case or padding as one visit. */
export const visitKey = (label: unknown): string =>
  String(label ?? "")
    .trim()
    .toLowerCase();

const asFiniteNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Canonical spelling for each visit key, chosen as the most frequently occurring
 * variant so the label does not flip between pages of the same dataset.
 * Alphabetical order breaks ties.
 */
export const buildVisitLabelMap = (
  rows: PlotRow[],
  dataMapping: string,
): Map<string, string> => {
  const counts = new Map<string, Map<string, number>>();

  for (const row of rows) {
    const raw = row?.[dataMapping];
    if (raw === null || raw === undefined || raw === "") continue;
    const key = visitKey(raw);
    if (!key) continue;
    const variants = counts.get(key) ?? new Map<string, number>();
    const label = String(raw);
    variants.set(label, (variants.get(label) ?? 0) + 1);
    counts.set(key, variants);
  }

  const canonical = new Map<string, string>();
  for (const [key, variants] of counts) {
    const [best] = [...variants.entries()].sort(
      (a, b) => b[1] - a[1] || a[0].localeCompare(b[0]),
    );
    canonical.set(key, best[0]);
  }
  return canonical;
};

/**
 * Protocol position of a visit label: `visit_order` when the backend supplied
 * it, else the cumulative `days` offset, else null (order unknown).
 */
export const visitSortKey = (
  label: string,
  rows: PlotRow[],
  dataMapping: string,
): number | null => {
  const key = visitKey(label);
  for (const row of rows) {
    if (visitKey(row?.[dataMapping]) !== key) continue;
    const order = asFiniteNumber(row.visit_order);
    if (order !== null) return order;
    const days = asFiniteNumber(row.days);
    if (days !== null) return days;
  }
  return null;
};

/**
 * Visit labels in protocol order. Visits whose position cannot be resolved keep
 * a deterministic place at the end (natural-order by label) rather than
 * scattering — the old code coerced a missing position to 0, which sorted
 * unknown visits in front of the screening visit.
 */
export const sortVisitDomain = (
  domain: string[],
  rows: PlotRow[],
  dataMapping: string,
): string[] =>
  [...domain]
    .map((label) => ({ label, order: visitSortKey(label, rows, dataMapping) }))
    .sort((a, b) => {
      if (a.order === null && b.order === null) {
        return a.label.localeCompare(b.label, undefined, { numeric: true });
      }
      if (a.order === null) return 1;
      if (b.order === null) return -1;
      return (
        a.order - b.order ||
        a.label.localeCompare(b.label, undefined, { numeric: true })
      );
    })
    .map((entry) => entry.label);
